import * as React from 'react';
import cx from 'classnames';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@common/components/modal";
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { getTestAttributes } from '@common/utils/tests';
import { I18n } from 'react-redux-i18n';
import { Button } from '@common/components/Button';
import { Title } from '../title';
import { QuestionnaireRiskSettings } from '@common/dto/questionnaire/risk-settings';
import { Flex } from '@common/components/Flex';
import { Checkbox } from '@common/components/checkbox';

import './risk-settings.styles.scss';

export type RiskSettingsProps = React.PropsWithChildren<{
  className?: string;
  close: () => void;
  onSubmit: (riskSettings: QuestionnaireRiskSettings) => void;
  riskSettings?: QuestionnaireRiskSettings;
}>;

export const RiskSettings: React.FC<RiskSettingsProps> = (props) => {
  const { className, close, riskSettings, onSubmit } = props;
  const [riskSettingsState, setRiskSettingsState] = React.useState(riskSettings || {
    determineRefCriteria: false,
    brcapro: false,
    mmrpro: false,
    pancpro: false,
    melapro: false,
    tyrerCuzick: false,
    premm: false,
    gail: false,
    claus: false,
    competingMortality: false,
    includeDataInputs: false,
  });

  const handleSubmit = React.useCallback(() => {
    onSubmit(riskSettingsState);
  }, [onSubmit, riskSettingsState]);

  const handleChange = React.useCallback((value: Partial<QuestionnaireRiskSettings>) => {
    setRiskSettingsState((prev) => ({
      ...prev,
      ...value
    }));
  }, []);

  const riskAssessmentModels: Array<Array<{ label: string; key: keyof QuestionnaireRiskSettings }>> = [
    [
      { label: I18n.t('BRCAPro'), key: 'brcapro' },
      { label: I18n.t('MMRPro'), key: 'mmrpro' },
      { label: I18n.t('PANCPRO'), key: 'pancpro' },
      { label: I18n.t('MelaPro'), key: 'melapro' },
    ],
    [
      { label: I18n.t('Tyrer-Cuzick'), key: 'tyrerCuzick' },
      { label: I18n.t('PREMM'), key: 'premm' },
      { label: I18n.t('Gail'), key: 'gail' },
      { label: I18n.t('Claus'), key: 'claus' },
    ]
  ];

  return (
    <Modal
      className={cx(
        'FhqDesignToolRiskSettings', 
        className
      )}
      show
      backdrop="static"
      bsClass="bootstrap modal"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close}>
        <ModalTitle
          {...getTestAttributes(MODULE_NAME, 'criteria-risk-settings', 'header')}
        >
          {I18n.t("Criteria and Risk Settings on Submit")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Title text={I18n.t('Criteria:')} />
        <Checkbox
          {...getTestAttributes(MODULE_NAME, 'criteria', 'checkbox')} 
          checked={riskSettingsState.determineRefCriteria} 
          className="FhqDesignToolRiskSettings__checkbox" 
          onChange={(checked) => handleChange({ determineRefCriteria: checked })}
        >
          {I18n.t('Determine criteria for referral/testing')}
        </Checkbox>

        <Title text={I18n.t('Risk Assessment Models:')} />
        <Flex alignItems="flex-start">
          {riskAssessmentModels.map((column, i) => 
            <div key={i} className="FhqDesignToolRiskSettings__column">
              {column.map((model) => 
                <Checkbox
                  {...getTestAttributes(MODULE_NAME, model.key, 'checkbox')}
                  key={model.key}
                  checked={riskSettingsState[model.key]}
                  className="FhqDesignToolRiskSettings__checkbox"
                  onChange={(checked) => handleChange({ [model.key]: checked })}
                >
                  {model.label}
                </Checkbox>
              )}
            </div>
          )}
        </Flex>

        <Checkbox
          {...getTestAttributes(MODULE_NAME, 'competing-mortality', 'checkbox')}
          disabled={!riskSettingsState.tyrerCuzick}
          checked={riskSettingsState.competingMortality}
          className="FhqDesignToolRiskSettings__checkbox"
          onChange={(checked) => handleChange({ competingMortality: checked })}
        >
          {I18n.t('Use Competing Mortality for Tyrer-Cuzick')}
        </Checkbox>

        <Checkbox
          {...getTestAttributes(MODULE_NAME, 'include-data-inputs', 'checkbox')}
          checked={riskSettingsState.includeDataInputs}
          className="FhqDesignToolRiskSettings__checkbox"
          onChange={(checked) => handleChange({ includeDataInputs: checked })}
        >
          {I18n.t('Include data inputs in reports')} 
        </Checkbox>
      </ModalBody>

      <ModalFooter>
        <Button
          {...getTestAttributes(MODULE_NAME, 'risk-settings-cancel', 'button')}
          onClick={close}
        >
          {I18n.t('Cancel')}
        </Button>
        <Button
          bsStyle="primary"
          onClick={handleSubmit}
          {...getTestAttributes(MODULE_NAME, 'risk-settings-submit', 'button')}
        >
          {I18n.t('Save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};